// Documents the board mints itself: quiz sets from the question bank or the AI assistant, and
// uploaded images/PDFs opened from Tải lên.
// `id` doubles as the `?unit=` value and therefore as the ink persistence documentKey.

import { create } from 'zustand';

// the .ts extension keeps this loadable by node --test, which does not resolve bare specifiers
import { onBoardOwnerChange, readBoardJson, writeBoardJson } from '../../stores/board/owner.ts';
import { quizPages, type QuizBody } from '../quiz/body.ts';
import { type MaybeMsg } from '../../i18n/msg.ts';

/** Generated quiz and upload documents, per teacher; the name predates uploads. */
const QUIZ_STORAGE_KEY = 'board-quizzes';

export type BoardDocumentKind = 'slide' | 'video' | 'quiz';

export interface BoardDocument {
  id: string;
  title: string;
  kind: BoardDocumentKind;
  /** Pages in this document - drives the toolbar pager readout. */
  pageCount: number;
  /** The painted quiz body - every generated `quiz` document carries its own questions. */
  quiz?: QuizBody;
  /** An uploaded file painted as the board background, so ink lands on it like on a lecture. */
  upload?: UploadSource;
  /** Text the board paints as pages itself, such as a student's written answer. */
  text?: TextPageSource;
}

export interface UploadSource {
  url: string;
  viewer: 'image' | 'pdf';
}

export interface TextPageSource {
  // MaybeMsg: the page text is translated when it is laid out, so an open page follows a language switch
  eyebrow: MaybeMsg;
  title: MaybeMsg;
  meta: MaybeMsg;
  /** CMS HTML, painted as plain paragraphs. */
  html: string;
  /** Shown when `html` has no text. */
  empty: MaybeMsg;
}

/** Prefix of every uploaded-file document id; the rest is the QLT upload id, so a re-open reuses its ink. */
export const UPLOAD_DOCUMENT_PREFIX = 'doc-upload-';

/** Prefix of a student submission document; the rest is the IZ homework id, so a re-open reuses its ink. */
export const SUBMISSION_DOCUMENT_PREFIX = 'doc-hw-';

export function isSubmissionDocumentId(id: string | null): boolean {
  return Boolean(id) && String(id).indexOf(SUBMISSION_DOCUMENT_PREFIX) === 0;
}

/** Stable small integer from a string, so a question set always hashes to the same id. */
export function fnv(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

/** Prefix of every generated quiz id, so a generated id can never shadow a real IZTeach unit. */
export const GENERATED_QUIZ_PREFIX = 'doc-quiz-';

/**
 * Identity of a question set. The document id is hashed off this, and the id is also the ink
 * documentKey: the same set must reopen the same ink layer, a different set must never share one.
 */
export function quizSignature(body: QuizBody): string {
  const parts: string[] = [];
  for (let i = 0; i < body.questions.length; i += 1) {
    const question = body.questions[i];
    // control separators, so no prompt or option text can forge a boundary and alias two sets
    parts.push(question.prompt + '\u001f' + question.options.join('\u001e') + '\u001f' + question.correct);
    // only view-only types carry these, so sets saved before them keep their ids
    if (question.stem) parts.push('\u001cstem' + question.stem);
    if (question.statementKeys) parts.push('\u001ckeys' + question.statementKeys.join(','));
    if (question.revealPrompt) parts.push('\u001creveal' + question.revealPrompt);
  }
  return parts.join('\u001d');
}

/**
 * A stable, collision-free id for a generated quiz.
 *
 * Stable: the same questions always hash to the same id, so re-importing a set reopens the
 * document the teacher already annotated. Collision-free: fnv is 32-bit, so a different set that
 * lands on a taken id takes the next free suffix rather than merging two documents' ink.
 */
export function resolveQuizDocumentId(body: QuizBody, existing: BoardDocument[]): string {
  const signature = quizSignature(body);
  const base = GENERATED_QUIZ_PREFIX + fnv(signature).toString(36);
  for (let attempt = 1; attempt < 1000; attempt += 1) {
    const candidate = attempt === 1 ? base : base + '-' + attempt;
    const taken = existing.filter((item) => item.id === candidate)[0];
    if (!taken) return candidate;
    if (taken.quiz && quizSignature(taken.quiz) === signature) return candidate;
  }
  // unreachable in practice; a distinct id still beats overwriting somebody else's ink layer
  return base + '-' + fnv(signature + '|overflow').toString(36);
}

/** Drops rows an older build wrote in a shape the painter can no longer read. */
export function usableQuizDocument(row: unknown): row is BoardDocument {
  const item = row as BoardDocument | null;
  if (!item || typeof item !== 'object') return false;
  if (typeof item.id !== 'string' || item.id.length === 0) return false;
  if (typeof item.title !== 'string' || item.kind !== 'quiz') return false;
  if (!item.quiz || !Array.isArray(item.quiz.questions)) return false;
  return typeof item.pageCount === 'number' && item.pageCount >= 1;
}

export function usableUploadDocument(row: unknown): row is BoardDocument {
  const item = row as BoardDocument | null;
  if (!item || typeof item !== 'object') return false;
  if (typeof item.id !== 'string' || item.id.indexOf(UPLOAD_DOCUMENT_PREFIX) !== 0) return false;
  if (typeof item.title !== 'string' || item.kind !== 'slide' || !item.upload) return false;
  return typeof item.upload.url === 'string' && item.upload.url.length > 0 && (item.upload.viewer === 'image' || item.upload.viewer === 'pdf');
}

/** The board document for an uploaded image or PDF; the PDF viewer sets the real page count. */
export function uploadDocument(file: { id: string; name: string; url: string }, viewer: UploadSource['viewer']): BoardDocument {
  return { id: UPLOAD_DOCUMENT_PREFIX + file.id, title: file.name, kind: 'slide', pageCount: 1, upload: { url: file.url, viewer } };
}

/** Builds the document entry for a question set without touching the store. */
export function quizDocument(body: QuizBody, existing: BoardDocument[]): BoardDocument {
  return {
    id: resolveQuizDocumentId(body, existing),
    title: body.title,
    kind: 'quiz',
    // derived, never restated - the painter paginates the same list
    pageCount: quizPages(body.questions).length,
    quiz: body,
  };
}

/** Quiz documents the teacher generated, persisted so a set survives a reload. */
export interface DynamicDocumentState {
  items: BoardDocument[];
  /** Creates or reopens the document for a question set and returns it. */
  openQuiz: (body: QuizBody) => BoardDocument;
  /** Adds an uploaded image/PDF as a document, or moves its existing entry to the end. */
  openUpload: (file: { id: string; name: string; url: string }, viewer: UploadSource['viewer']) => BoardDocument;
  remove: (id: string) => void;
  clear: () => void;
  /** Student submissions: in memory only, never on the strip or in storage. */
  transient: BoardDocument[];
  openTransient: (document: BoardDocument) => BoardDocument;
  clearTransient: () => void;
}

function hydrate(): BoardDocument[] {
  const stored = readBoardJson<unknown>(QUIZ_STORAGE_KEY);
  return Array.isArray(stored) ? stored.filter((row) => usableQuizDocument(row) || usableUploadDocument(row)) : [];
}

export const useDynamicDocumentStore = create<DynamicDocumentState>((set, get) => ({
  items: hydrate(),

  openQuiz(body) {
    const items = get().items;
    const document = quizDocument(body, items);
    const kept = items.filter((item) => item.id !== document.id);
    const next = kept.concat([document]);
    set({ items: next });
    writeBoardJson(QUIZ_STORAGE_KEY, next);
    return document;
  },

  openUpload(file, viewer) {
    const document = uploadDocument(file, viewer);
    const next = get().items.filter((item) => item.id !== document.id).concat([document]);
    set({ items: next });
    writeBoardJson(QUIZ_STORAGE_KEY, next);
    return document;
  },

  remove(id) {
    const next = get().items.filter((item) => item.id !== id);
    set({ items: next });
    writeBoardJson(QUIZ_STORAGE_KEY, next);
  },

  clear() {
    set({ items: [] });
    writeBoardJson(QUIZ_STORAGE_KEY, []);
  },

  transient: [],

  openTransient(document) {
    set({ transient: get().transient.filter((item) => item.id !== document.id).concat([document]) });
    return document;
  },

  clearTransient() {
    set({ transient: [] });
  },
}));

onBoardOwnerChange(() => {
  useDynamicDocumentStore.setState({ items: hydrate(), transient: [] });
});

/** The generated document for a `?unit=` value, or null when the id is a real IZTeach unit. */
export function findGeneratedDocument(id: string | null): BoardDocument | null {
  if (!id) return null;
  const state = useDynamicDocumentStore.getState();
  const hits = state.items.concat(state.transient).filter((item) => item.id === id);
  return hits.length > 0 ? hits[0] : null;
}

